import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Sheet, SheetContent } from '../components/ui/sheet';
import {
  Users, Briefcase, TrendingUp, Clock, CheckCircle2,
  BarChart3, Building2, ArrowRight, PoundSterling, Globe, Menu, LogOut,
  Search, Filter, FileText, Download, Calendar, X
} from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: 'bg-gray-100 text-gray-700' },
  { value: 'assigned', label: 'Assigned', color: 'bg-blue-100 text-blue-700' },
  { value: 'contacted', label: 'Contacted', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'under_review', label: 'Under Review', color: 'bg-amber-100 text-amber-700' },
  { value: 'candidate_search', label: 'Candidate Search', color: 'bg-purple-100 text-purple-700' },
  { value: 'interviews', label: 'Interviews', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'offer_sent', label: 'Offer Sent', color: 'bg-pink-100 text-pink-700' },
  { value: 'placement_completed', label: 'Placement Completed', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'closed', label: 'Closed', color: 'bg-slate-100 text-slate-700' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-700' },
];

const statusMeta = (s) => STATUS_OPTIONS.find(o => o.value === s) || { label: s, color: 'bg-gray-100 text-gray-700' };

export default function RecruitmentServicesDashboard() {
  const { user, api, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = () => { logout(); navigate('/'); };
  const [requests, setRequests] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [filters, setFilters] = useState({ company: '', job_title: '', country: '', industry: '', recruiter: '', priority: '' });
  const [selected, setSelected] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const [followUpDraft, setFollowUpDraft] = useState('');

  useEffect(() => {
    if (!['admin', 'recruiter'].includes(user?.user_type)) {
      navigate('/');
      return;
    }
    fetchRequests();
    fetchRecruiters();
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRequests = async (extraFilters = filters) => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(extraFilters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get('/admin/recruitment-requests', { params: { ...params, limit: 200 } });
      setRequests(res.data.requests || []);
    } catch {
      toast.error('Failed to load recruitment requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecruiters = async () => {
    try {
      const res = await api.get('/admin/users', { params: { user_type: 'recruiter', limit: 200 } });
      setRecruiters(res.data.users || res.data || []);
    } catch {
      setRecruiters([]);
    }
  };

  const fetchReport = async () => {
    try {
      const res = await api.get('/admin/recruitment-requests/report');
      setReport(res.data);
    } catch {
      setReport(null);
    }
  };

  const recruiterName = (id) => recruiters.find(r => r.id === id)?.full_name || (id ? id.slice(0, 8) : 'Unassigned');

  const updateStatus = async (reqId, status) => {
    try {
      await api.put(`/admin/recruitment-requests/${reqId}/status?status=${status}`);
      toast.success('Status updated');
      fetchRequests();
      fetchReport();
      if (selected?.id === reqId) setSelected(prev => ({ ...prev, status }));
    } catch {
      toast.error('Failed to update status');
    }
  };

  const assignRecruiter = async (reqId, recruiterId) => {
    try {
      await api.put(`/admin/recruitment-requests/${reqId}/assign`, { recruiter_id: recruiterId });
      toast.success('Recruiter assigned');
      fetchRequests();
      if (selected?.id === reqId) setSelected(prev => ({ ...prev, assigned_recruiter_id: recruiterId }));
    } catch {
      toast.error('Failed to assign recruiter');
    }
  };

  const saveNotes = async () => {
    if (!selected) return;
    try {
      await api.put(`/admin/recruitment-requests/${selected.id}/notes`, { notes: notesDraft });
      toast.success('Notes saved');
      fetchRequests();
    } catch {
      toast.error('Failed to save notes');
    }
  };

  const saveFollowUp = async () => {
    if (!selected) return;
    try {
      await api.put(`/admin/recruitment-requests/${selected.id}/follow-up`, { follow_up_date: followUpDraft });
      toast.success('Follow-up date set');
      fetchRequests();
    } catch {
      toast.error('Failed to set follow-up date');
    }
  };

  const openDetail = (req) => {
    setSelected(req);
    setNotesDraft(req.notes || '');
    setFollowUpDraft(req.follow_up_date || '');
    setShowDetail(true);
  };

  const applyFilters = (e) => {
    e.preventDefault();
    fetchRequests();
  };

  const clearFilters = () => {
    const cleared = { company: '', job_title: '', country: '', industry: '', recruiter: '', priority: '' };
    setFilters(cleared);
    fetchRequests(cleared);
  };

  const stats = {
    total: requests.length,
    new: requests.filter(r => r.status === 'new').length,
    active: requests.filter(r => !['placement_completed', 'closed', 'cancelled'].includes(r.status)).length,
    placement_completed: requests.filter(r => r.status === 'placement_completed').length,
  };

  const filtered = activeTab === 'all' ? requests
    : activeTab === 'active' ? requests.filter(r => !['placement_completed', 'closed', 'cancelled'].includes(r.status))
    : requests.filter(r => r.status === activeTab);

  const TABS = [
    { id: 'all', label: 'All Requests', icon: Briefcase },
    { id: 'new', label: 'New', icon: Clock },
    { id: 'active', label: 'Active Pipeline', icon: TrendingUp },
    { id: 'placement_completed', label: 'Completed', icon: CheckCircle2 },
  ];

  return (
    <div className="min-h-screen bg-[#F0F4F8]">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-purple-700 min-h-screen sticky top-0 hidden lg:block">
          <div className="p-6">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white font-bold">Recruitment</p>
                <p className="text-white/60 text-xs">Direct Employment Solutions</p>
              </div>
            </div>
            <nav className="space-y-1">
              {TABS.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === tab.id ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <tab.icon className="w-5 h-5" />
                  {tab.label}
                  {tab.id !== 'all' && stats[tab.id] > 0 && (
                    <span className="ml-auto bg-white/20 text-white text-xs px-2 py-0.5 rounded-full">
                      {stats[tab.id]}
                    </span>
                  )}
                </button>
              ))}
            </nav>

            <div className="mt-8 border-t border-white/10 pt-6 space-y-2">
              <Link to="/admin">
                <Button variant="ghost" className="w-full text-white/70 hover:text-white hover:bg-white/10 justify-start">
                  <BarChart3 className="w-4 h-4 mr-2" />Admin Panel
                </Button>
              </Link>
              <Link to="/direct-employment-solutions">
                <Button variant="ghost" className="w-full text-white/70 hover:text-white hover:bg-white/10 justify-start">
                  <Globe className="w-4 h-4 mr-2" />Landing Page
                </Button>
              </Link>
              <Button variant="ghost" className="w-full text-white/70 hover:text-white hover:bg-white/10 justify-start" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />Logout
              </Button>
            </div>
          </div>
        </aside>

        {/* Mobile Menu Sheet */}
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetContent side="left" className="w-64 p-0 bg-purple-700 border-0">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                  <Users className="w-4 h-4 text-white" />
                </div>
                <p className="text-white font-bold text-sm">Recruitment</p>
              </div>
              <nav className="space-y-1">
                {TABS.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => { setActiveTab(tab.id); setMobileMenuOpen(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      activeTab === tab.id ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <tab.icon className="w-5 h-5" />
                    {tab.label}
                  </button>
                ))}
                <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors text-white/60 hover:text-white hover:bg-white/5">
                  <LogOut className="w-5 h-5" />
                  Logout
                </button>
              </nav>
            </div>
          </SheetContent>
        </Sheet>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8 pb-8">
          {/* Mobile header */}
          <div className="lg:hidden flex items-center gap-3 mb-6 pb-4 border-b">
            <button onClick={() => setMobileMenuOpen(true)} className="p-2 rounded-lg hover:bg-gray-100">
              <Menu className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-bold">{TABS.find(t => t.id === activeTab)?.label}</h2>
          </div>
          <div className="max-w-6xl">
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-foreground">Direct Employment Solutions</h1>
              <p className="text-muted-foreground mt-1">
                Manage employer requests for custom recruitment (15% placement fee model)
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {[
                { label: 'Total Requests', value: report?.total_requests ?? stats.total, color: 'text-blue-600', bg: 'bg-blue-100', icon: Briefcase },
                { label: 'New', value: stats.new, color: 'text-amber-600', bg: 'bg-amber-100', icon: Clock },
                { label: 'In Active Pipeline', value: stats.active, color: 'text-purple-600', bg: 'bg-purple-100', icon: TrendingUp },
                { label: 'Placements Completed', value: report?.placements_completed ?? stats.placement_completed, color: 'text-emerald-600', bg: 'bg-emerald-100', icon: CheckCircle2 },
              ].map((s, i) => (
                <Card key={i}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">{s.label}</p>
                        <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                      </div>
                      <div className={`w-10 h-10 rounded-lg ${s.bg} flex items-center justify-center`}>
                        <s.icon className={`w-5 h-5 ${s.color}`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {report?.avg_turnaround_days != null && (
              <p className="text-sm text-muted-foreground mb-6">
                Average turnaround time for completed placements: <strong>{report.avg_turnaround_days} days</strong>
              </p>
            )}

            {/* Filters */}
            <Card className="mb-6">
              <CardContent className="p-4">
                <form onSubmit={applyFilters} className="flex flex-wrap gap-3 items-end">
                  <div className="flex items-center gap-2 flex-1 min-w-[160px]">
                    <Search className="w-4 h-4 text-muted-foreground shrink-0" />
                    <Input placeholder="Company" value={filters.company} onChange={(e) => setFilters(f => ({ ...f, company: e.target.value }))} />
                  </div>
                  <Input placeholder="Job title" className="w-40" value={filters.job_title} onChange={(e) => setFilters(f => ({ ...f, job_title: e.target.value }))} />
                  <Input placeholder="Country" className="w-32" value={filters.country} onChange={(e) => setFilters(f => ({ ...f, country: e.target.value }))} />
                  <Input placeholder="Industry" className="w-32" value={filters.industry} onChange={(e) => setFilters(f => ({ ...f, industry: e.target.value }))} />
                  <Select value={filters.recruiter || 'all'} onValueChange={(v) => setFilters(f => ({ ...f, recruiter: v === 'all' ? '' : v }))}>
                    <SelectTrigger className="w-40"><SelectValue placeholder="Recruiter" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Recruiters</SelectItem>
                      {recruiters.map(r => <SelectItem key={r.id} value={r.id}>{r.full_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={filters.priority || 'all'} onValueChange={(v) => setFilters(f => ({ ...f, priority: v === 'all' ? '' : v }))}>
                    <SelectTrigger className="w-32"><SelectValue placeholder="Priority" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Priorities</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button type="submit" size="sm" className="bg-purple-600 hover:bg-purple-700">
                    <Filter className="w-4 h-4 mr-2" />Apply
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={clearFilters}>
                    <X className="w-4 h-4 mr-1" />Clear
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Requests List */}
            {loading ? (
              <div className="space-y-4">
                {Array(3).fill(0).map((_, i) => <Skeleton key={i} className="h-40" />)}
              </div>
            ) : filtered.length === 0 ? (
              <Card>
                <CardContent className="py-16 text-center">
                  <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">No requests found</h3>
                  <p className="text-muted-foreground">
                    Employers can submit requests from the Direct Employment Solutions landing page.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {filtered.map(req => (
                  <Card key={req.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => openDetail(req)}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center shrink-0">
                            <Building2 className="w-6 h-6 text-purple-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">{req.job_title || 'Recruitment Request'}</h3>
                            <p className="text-muted-foreground">{req.company_name}</p>
                            <p className="text-xs text-muted-foreground font-mono">{req.reference_code}</p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <Badge className={statusMeta(req.status).color}>{statusMeta(req.status).label}</Badge>
                          {req.used_credit && <Badge className="bg-emerald-100 text-emerald-700">Paid</Badge>}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm mb-4">
                        <div>
                          <span className="text-muted-foreground">Location</span>
                          <p className="font-medium">{req.location || 'Not specified'}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Positions</span>
                          <p className="font-medium">{req.positions_count || 1}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Fee</span>
                          <p className="font-medium flex items-center gap-1">
                            <PoundSterling className="w-3.5 h-3.5" />{req.fee_percentage || 15}%
                          </p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Recruiter</span>
                          <p className="font-medium">{recruiterName(req.assigned_recruiter_id)}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Submitted</span>
                          <p className="font-medium">{req.created_at?.slice(0, 10)}</p>
                        </div>
                      </div>

                      {(req.requirements || []).length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {req.requirements.slice(0, 6).map((r, i) => (
                            <Badge key={i} variant="outline" className="text-xs">{r}</Badge>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Detail Modal */}
      {showDetail && selected && (
        <Dialog open={showDetail} onOpenChange={setShowDetail}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selected.job_title} — {selected.company_name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              <div className="flex items-center gap-3 flex-wrap">
                <Badge className={statusMeta(selected.status).color}>{statusMeta(selected.status).label}</Badge>
                <span className="text-xs font-mono text-muted-foreground">{selected.reference_code}</span>
              </div>

              {/* Pipeline controls */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Status</p>
                  <Select value={selected.status} onValueChange={(v) => updateStatus(selected.id, v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Assigned Recruiter</p>
                  <Select value={selected.assigned_recruiter_id || ''} onValueChange={(v) => assignRecruiter(selected.id, v)}>
                    <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent>
                      {recruiters.map(r => <SelectItem key={r.id} value={r.id}>{r.full_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1 flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />Follow-up Date</p>
                  <div className="flex gap-2">
                    <Input type="date" value={followUpDraft} onChange={(e) => setFollowUpDraft(e.target.value)} />
                    <Button size="sm" variant="outline" onClick={saveFollowUp}>Save</Button>
                  </div>
                </div>
              </div>

              {/* Company info */}
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-2">Company Information</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-muted-foreground">Contact:</span> {selected.contact_person || 'N/A'} ({selected.contact_position || 'N/A'})</div>
                  <div><span className="text-muted-foreground">Email:</span> {selected.contact_email || 'N/A'}</div>
                  <div><span className="text-muted-foreground">Phone:</span> {selected.contact_phone || 'N/A'}</div>
                  <div><span className="text-muted-foreground">Website:</span> {selected.company_website || 'N/A'}</div>
                  <div><span className="text-muted-foreground">Industry:</span> {selected.industry || 'N/A'}</div>
                  <div><span className="text-muted-foreground">Company Size:</span> {selected.company_size || 'N/A'}</div>
                  <div><span className="text-muted-foreground">Country/Region:</span> {selected.country || 'N/A'} {selected.state_region ? `— ${selected.state_region}` : ''}</div>
                  <div><span className="text-muted-foreground">Address:</span> {selected.company_address || 'N/A'}</div>
                </div>
              </div>

              {/* Role info */}
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-2">Recruitment Requirements</h4>
                <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                  <div><span className="text-muted-foreground">Employment Type:</span> {selected.employment_type}</div>
                  <div><span className="text-muted-foreground">Department:</span> {selected.department || 'N/A'}</div>
                  <div><span className="text-muted-foreground">Positions:</span> {selected.positions_count}</div>
                  <div><span className="text-muted-foreground">Location:</span> {selected.location}</div>
                  <div><span className="text-muted-foreground">Salary Range:</span> {selected.salary_range || 'N/A'}</div>
                  <div><span className="text-muted-foreground">Experience:</span> {selected.years_experience || 'N/A'}</div>
                </div>
                <p className="text-sm bg-gray-50 p-3 rounded-lg whitespace-pre-wrap">{selected.job_description}</p>
              </div>

              {/* Documents */}
              {(selected.documents || []).length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-2">Documents</h4>
                  <div className="space-y-2">
                    {selected.documents.map((doc, i) => (
                      <a key={i} href={`${process.env.REACT_APP_BACKEND_URL}${doc.url}`} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-[#003366] hover:underline">
                        <FileText className="w-4 h-4" />{doc.name}
                        <Badge variant="outline" className="text-xs">{doc.category}</Badge>
                        <Download className="w-3.5 h-3.5 ml-auto" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Internal notes */}
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-2">Internal Notes</h4>
                <textarea
                  className="w-full border rounded-md p-3 text-sm min-h-[80px]"
                  value={notesDraft}
                  onChange={(e) => setNotesDraft(e.target.value)}
                  placeholder="Internal notes visible to admins/recruiters only..."
                />
                <Button size="sm" variant="outline" className="mt-2" onClick={saveNotes}>Save Notes</Button>
              </div>

              {/* Timeline */}
              {(selected.timeline || []).length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-2">Activity Timeline</h4>
                  <div className="space-y-2">
                    {[...selected.timeline].reverse().map((t, i) => (
                      <div key={i} className="text-sm flex items-start gap-2">
                        <span className="text-muted-foreground shrink-0">{t.created_at?.slice(0, 16).replace('T', ' ')}</span>
                        <span className="font-medium">{t.event?.replace(/_/g, ' ')}</span>
                        {t.note && <span className="text-muted-foreground">— {t.note}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end pt-2">
                <Button variant="outline" onClick={() => setShowDetail(false)}>Close</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
